import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import { Calendar, MessageSquare, CalendarPlus, Clock, X } from "lucide-react";

const ICON_OPTIONS = [
  { value: "calendar", label: "Calendar", Icon: Calendar },
  { value: "message", label: "Message", Icon: MessageSquare },
  { value: "calendar-plus", label: "Calendar+", Icon: CalendarPlus },
  { value: "clock", label: "Clock", Icon: Clock },
];

const POSITION_OPTIONS = [
  { value: "bottom-right", labelKey: "widget.pos_br" },
  { value: "bottom-left", labelKey: "widget.pos_bl" },
];

const LANGUAGE_OPTIONS = [
  { value: "auto", label: "Auto (visitor browser)" },
  { value: "en", label: "English" },
  { value: "nl", label: "Nederlands" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
];

const THEME_OPTIONS = [
  { value: "auto", label: "Auto (visitor system)" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

const WidgetSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const shopId = profile?.organisation_id || "YOUR_SHOP_ID";

  const [bgColor, setBgColor] = useState("#6366f1");
  const [iconColor, setIconColor] = useState("#ffffff");
  const [icon, setIcon] = useState("calendar");
  const [position, setPosition] = useState("bottom-right");
  const [autoPopup, setAutoPopup] = useState(false);
  const [autoPopupDelay, setAutoPopupDelay] = useState(5);
  const [greeting, setGreeting] = useState("");
  const [lang, setLang] = useState("auto");
  const [theme, setTheme] = useState("auto");

  const embedScript = useMemo(() => {
    const origin = window.location.origin;
    const attrs: Record<string, string> = {
      "data-shop": shopId,
      "data-bg": bgColor,
      "data-fg": iconColor,
      "data-icon": icon,
      "data-pos": position,
    };
    if (autoPopup) {
      attrs["data-auto"] = "1";
      attrs["data-delay"] = String(autoPopupDelay);
    }
    if (greeting) attrs["data-greet"] = greeting;
    if (lang !== "auto") attrs["data-lang"] = lang;
    if (theme !== "auto") attrs["data-theme"] = theme;
    const attrStr = Object.entries(attrs)
      .map(([k, v]) => `${k}="${String(v).replace(/"/g, "&quot;")}"`)
      .join(" ");
    return `<script async src="${origin}/widget.js" ${attrStr}></\u0073cript>`;
  }, [shopId, bgColor, iconColor, icon, position, autoPopup, autoPopupDelay, greeting, lang, theme]);

  const copyCode = () => {
    navigator.clipboard.writeText(embedScript);
    toast.success(t("widget.copied"));
  };

  const selectedIcon = ICON_OPTIONS.find((i) => i.value === icon) || ICON_OPTIONS[0];

  return (
    <>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("widget.title")}</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration */}
        <div className="space-y-6">
          {/* Appearance */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.appearance")}</h2>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("widget.bg_color")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-input"
                  />
                  <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("widget.icon_color")}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={iconColor}
                    onChange={(e) => setIconColor(e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-input"
                  />
                  <Input value={iconColor} onChange={(e) => setIconColor(e.target.value)} className="font-mono text-xs" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("widget.icon")}</Label>
              <div className="flex gap-2">
                {ICON_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setIcon(opt.value)}
                    className={`flex h-12 w-12 items-center justify-center rounded-lg border-2 transition-colors ${
                      icon === opt.value
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-background text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <opt.Icon className="h-5 w-5" />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("widget.position")}</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSITION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {THEME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Behavior */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-4">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.behavior")}</h2>

            <div className="space-y-2">
              <Label>{t("widget.greeting_text")}</Label>
              <Input
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder={t("widget.greeting_placeholder")}
              />
              <p className="text-xs text-muted-foreground">{t("widget.greeting_help")}</p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{t("widget.auto_popup")}</p>
                <p className="text-xs text-muted-foreground">{t("widget.auto_popup_desc")}</p>
              </div>
              <Switch checked={autoPopup} onCheckedChange={setAutoPopup} />
            </div>

            {autoPopup && (
              <div className="space-y-2">
                <Label>{t("widget.auto_popup_delay")}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={autoPopupDelay}
                    onChange={(e) => setAutoPopupDelay(Number(e.target.value))}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">{t("widget.seconds")}</span>
                </div>
              </div>
            )}
          </section>

          {/* Embed code */}
          <section className="rounded-xl border border-border bg-card p-6 shadow-card space-y-3">
            <h2 className="text-sm font-semibold text-card-foreground">{t("widget.embed_code")}</h2>
            <p className="text-xs text-muted-foreground">{t("widget.embed_desc")}</p>
            <div className="rounded-lg bg-muted p-3 max-h-40 overflow-auto">
              <code className="text-xs text-foreground break-all whitespace-pre-wrap">{embedScript}</code>
            </div>
            <Button variant="outline" onClick={copyCode}>
              {t("widget.copy_code")}
            </Button>
          </section>
        </div>

        {/* Live Preview */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-card-foreground">{t("widget.preview")}</h2>
          <div className="relative rounded-xl border border-border bg-muted/30 overflow-hidden" style={{ height: 500 }}>
            {/* Simulated webpage background */}
            <div className="absolute inset-0 p-6">
              <div className="h-4 w-32 rounded bg-muted mb-4" />
              <div className="h-3 w-full rounded bg-muted/60 mb-2" />
              <div className="h-3 w-3/4 rounded bg-muted/60 mb-2" />
              <div className="h-3 w-5/6 rounded bg-muted/60 mb-6" />
              <div className="h-24 w-full rounded-lg bg-muted/40 mb-4" />
              <div className="h-3 w-full rounded bg-muted/60 mb-2" />
              <div className="h-3 w-2/3 rounded bg-muted/60" />
            </div>

            {/* Greeting bubble */}
            {greeting && (
              <div
                className="absolute z-10 bg-card rounded-xl shadow-lg p-3 text-xs text-card-foreground max-w-[200px]"
                style={{
                  [position === "bottom-left" ? "left" : "right"]: 16,
                  bottom: 80,
                }}
              >
                {greeting}
                <button className="absolute top-1 right-2 text-muted-foreground text-xs">×</button>
              </div>
            )}

            {/* Widget button */}
            <button
              className="absolute z-10 flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105"
              style={{
                background: bgColor,
                color: iconColor,
                [position === "bottom-left" ? "left" : "right"]: 16,
                bottom: 16,
              }}
            >
              <selectedIcon.Icon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WidgetSettings;
